import { ReactNode, useMemo, useRef, useState } from "react";
import {
  DatePicker,
  Descriptions,
  DescriptionsProps,
  FormInstance,
  Input,
  InputNumber,
  ModalProps,
} from "antd";
import { useTranslation } from "react-i18next";
import { docFetch } from "@/apis/doc";
import { getFileUrl } from "@/apis/file"
import SModalForm from "@/styles/SModalForm";
import { Customer, Price, PriceAdd } from "@/types/sales";
import SInputNumber from "./SInputNum";
import dayjs from "dayjs";
import { PriceFormType } from "../types/priceType";
import { priceFormat } from "../../../utils/format";
import Dict from "@/components/Dict";
import { DictEnum } from "@/types/dict";
import { Product } from "@/types/product";
import { CreditLevelSelect, CurrencySelect, CountrySelect, ProductSelect } from "@/components/Selector";

interface PriceFormProps extends ModalProps {
  handleCancel: () => void;
  handleOk: (data: PriceAdd) => Promise<void>;
  data: Price | null;
  leftTitle: ReactNode;
  confirmLoading: boolean;
  customer: Customer;
}

const PriceForm = ({ handleOk, data, ...props }: PriceFormProps) => {
  const { t } = useTranslation();
  const formRef = useRef<FormInstance<PriceFormType>>(null);
  const [spinning, setSpinning] = useState<boolean>(false)
  const [picUrl, setPicUrl] = useState<string | undefined>('')

  const customer: DescriptionsProps["items"] = [
    {
      key: "code",
      label: t("sale.code"),
      children: props?.customer?.code,
    },
    {
      key: "name1",
      label: t("sale.name1"),
      children: props?.customer?.name1,
    },
    {
      key: "name2",
      label: t("sale.name2"),
      children: props?.customer?.name2,
    },
    {
      key: "shortName",
      label: t("sale.shortName"),
      children: props?.customer?.shortName,
    },
    {
      key: "country",
      label: t("sale.country"),
      children: (
        <CountrySelect
          key={props?.customer?.id + "country"}
          readOnly
          value={props?.customer?.country ?? ""}
        />
      ),
    },
    {
      key: "address",
      label: t("sale.address"),
      children: props?.customer?.address,
    },
    {
      key: "zipCode",
      label: t("sale.zipCode"),
      children: props?.customer?.zipCode,
    },
    {
      key: "email",
      label: t("sale.email"),
      children: props?.customer?.email,
    },
    {
      key: "contact",
      label: t("sale.contact"),
      children: props?.customer?.contact,
    },
    {
      key: "phone",
      label: t("sale.phone"),
      children: props?.customer?.phone,
    },
    {
      key: "mobile",
      label: t("sale.mobile"),
      children: props?.customer?.mobile,
    },
    {
      key: "fax",
      label: t("sale.fax"),
      children: props?.customer?.fax,
    },
    {
      key: "account1",
      label: t("sale.account1"),
      children: props?.customer?.account1,
    },
    {
      key: "bank1",
      label: t("sale.bank1"),
      children: props?.customer?.bank1,
    },
    {
      key: "swiftCode1",
      label: t("sale.swiftCode1"),
      children: props?.customer?.swiftCode1,
    },
    {
      key: "account2",
      label: t("sale.account2"),
      children: props?.customer?.account2,
    },
    {
      key: "bank2",
      label: t("sale.bank2"),
      children: props?.customer?.bank2,
    },
    {
      key: "swiftCode2",
      label: t("sale.swiftCode2"),
      children: props?.customer?.swiftCode2,
    },
    {
      key: "currency",
      label: t("sale.currency"),
      children: (
        <CurrencySelect
          key={props?.customer?.id + "currency"}
          readOnly
          value={props?.customer?.currency ?? ""}
        />
      ),
    },
    {
      key: "saleType",
      label: t("sale.saleType"),
      children: (
        <Dict
          type={DictEnum.SALE_TYPE}
          value={props?.customer?.saleType ?? ""}
          key={props?.customer?.id + "saleType"}
          readOnly
        />
      ),
    },
    {
      key: "creditLevel",
      label: t("sale.creditLevel"),
      children: (
        <CreditLevelSelect
          key={props?.customer?.id + "creditLevel"}
          readOnly
          value={props?.customer?.creditLevel ?? ""}
        />
      ),
    },
    {
      key: "creditLevel",
      label: t("sale.creditLine"),
      children: props?.customer?.creditLine,
    },
    {
      key: "depositRate",
      label: t("sale.depositRate"),
      children: props?.customer?.depositRate
        ? props?.customer?.depositRate + "%"
        : "",
    },
    {
      key: "tradeTerm",
      label: t("sale.tradeTerm"),
      children: (
        <Dict
          type={DictEnum.SALE_TRADE_TERM}
          value={props?.customer?.tradeTerm ?? ""}
          key={props?.customer?.id + "tradeTerm"}
          readOnly
        />
      ),
    },
    {
      key: "creditLevel",
      label: t("sale.payMethod"),
      children: (
        <Dict
          type={DictEnum.SALE_PAY_METHOD}
          value={props?.customer?.payMethod ?? ""}
          key={props?.customer?.id + "payMethod"}
          readOnly
        />
      ),
    },
    {
      key: "taxRate",
      label: t("sale.taxRate"),
      children: props?.customer?.taxRate ? props?.customer?.taxRate + "%" : "",
    },
  ];

  const product: DescriptionsProps["items"] = [
    {
      key: "code",
      label: t("product.code"),
      children: data?.product?.code,
    },
    {
      key: "name",
      label: t("product.name"),
      children: data?.product?.name,
    },
    {
      key: "model",
      label: t("product.brand1"),
      children: data?.product?.model?.toLocaleUpperCase(),
    },
    {
      key: "model2",
      label: t("product.brand2"),
      children: data?.product?.t1?.toLocaleUpperCase(),
    },
  ];

  const detail: PriceFormType | null = useMemo(() => {
    if (data) {
      return {
        ...data,
        expiredAt: data.expiredAt ? dayjs(data.expiredAt) : null,
      }
    } else {
      return null
    }
  }, [data])

  const getList = (list: Array<Product>, e: string) => {
    let d = list.filter(item => item.id === e)
    // @ts-ignore
    let picDocId: string = d[0].picDocId
    setSpinning(true)
    docFetch(picDocId).then((doc: any) => {
      let pic: string | undefined = ''
      for (let i = 0; i < doc.subs.length; i++) {
        if (doc.subs[i].fileId) {
          pic = getFileUrl(doc.subs[i].fileId)
          break
        }
      }
      setPicUrl(pic)
      setSpinning(false)
    })
  }

  const handlePic = (product: Product | null) => {
    if (product) {
      let docId = product.picDocId
      setSpinning(true)
      docFetch(docId ?? '').then((doc) => {
        let pic: string | undefined = ''
        for (let i = 0; i < doc.subs.length; i++) {
          if (doc.subs[i].fileId) {
            pic = getFileUrl(doc.subs[i].fileId)
            break
          }
        }
        setPicUrl(pic)
      })
        .catch(() => undefined)
        .finally(() => setSpinning(false))
    }
  };

  return (
    <SModalForm<PriceFormType>
      spinning={spinning}
      picUrl={picUrl}
      data={detail} width={1200}
      modelFormRef={formRef}
      showPic={true}
      setPicUrl={() => setPicUrl('')}
      formProps={{ style: { position: 'relative' } }}
      formItems={[
        {
          label: t("sale.product"),
          name: "productId",
          children: <ProductSelect width="80%" getSelectPro={handlePic} onlyOrg />,
          required: true,
          hidden: !!data,
          rules: [{ required: true, message: t("components.plProduct") }],
        },
        {
          label: t("sale.taxPrice"),
          name: "price",
          children: (
            <SInputNumber
              width='80%'
              placeholder={t("sale.plTaxPrice")}
              maxTip={t("sale.priceMax", { num: priceFormat('100000000') })}
              minTip={t("sale.priceMin", { num: priceFormat('0') })}
            />
          ),
          required: true,
          rules: [{ required: true, message: t("sale.plTaxPrice") }],
        },
        {
          label: t("sale.estimateDays"),
          name: "estimateDays",
          children: (
            <InputNumber changeOnWheel
              placeholder={t("sale.plEstimateDays")}
              min={0}
              style={{ width: '80%' }}
              precision={0} />
          ),
        },
        {
          label: t("sale.expiredAt"),
          name: "expiredAt",
          children: (
            <DatePicker
              placeholder={t("sale.plExpiredAt")}
              style={{ width: '80%' }} />
          ),
        },
        {
          label: t("sale.customerModel"),
          name: "customerModel",
          initialValue: '',
          children: <Input placeholder={t("sale.plCustomerModel")} maxLength={50} showCount />,
        },
        {
          label: t("sale.mfgr"),
          name: "mfgr",
          initialValue: '',
          children: <Input placeholder={t("sale.plMfgr")} maxLength={50} showCount />,
        },
        {
          label: t("common.desc"),
          name: "desc",
          initialValue: '',
          children: (
            <Input.TextArea
              placeholder={t("common.plDesc")}
              maxLength={100}
              showCount
              autoSize={{ maxRows: 6, minRows: 4 }} />
          ),
        },
        !!data
          ? {
            children: (
              <Descriptions title={t("sale.productInfo")} bordered items={product} />
            ),
          }
          : {},
        {
          children: <Descriptions title={t("sale.customerInfo")} bordered items={customer} />,
        },
      ]}
      handleOk={(data) => {
        const detail = {
          ...data,
          expiredAt: data.expiredAt ? dayjs(data.expiredAt).startOf('day').valueOf() : null
        };
        return handleOk(detail);
      }}
      {...props}
    />
  );
};

export default PriceForm;
